import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
} from '@mui/material';

import { Location } from '../../types';
import { subMonths } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { saveAs } from 'file-saver';
import { useAPICall } from 'hooks/useAPICall';
import { getWeatherExport } from 'features/weather/api/getWeatherExport';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

interface Props {
  open: boolean;
  location: Location | undefined;
  handleClose: () => void;
}

const ExportWeatherDialog = ({ open, location, handleClose }: Props) => {
  const { dateFormatWithZeros, timeFormat, ampm, timezone } = useUserDateTime();
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isSubmitted },
  } = useForm({
    defaultValues: {
      fromDate: subMonths(new Date(), 6),
      toDate: new Date(),
    },
  });

  const { safeCall: safeGetWeatherExport, isLoading } =
    useAPICall(getWeatherExport);

  const onSubmit = async () => {
    if (!location) return;
    const form = getValues();
    const fileName = `spectras-weather-history-export_${formatInTimeZone(
      new Date(),
      timezone,
      'yyyy-MM-dd_hh-mm-ss_aa'
    )}.xlsx`;
    const exportedFile = await safeGetWeatherExport(
      location.id,
      zonedTimeToUtc(form.fromDate, timezone).toISOString(),
      zonedTimeToUtc(form.toDate, timezone).toISOString()
    );
    if (exportedFile) saveAs(exportedFile, fileName);
  };

  if (!location) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>{'Export Weather for ' + location.name}</DialogTitle>
      <DialogContent>
        <Grid container sx={{ pt: '1rem' }} spacing={1} justifyContent='center'>
          <Grid item>
            <Controller
              name='fromDate'
              control={control}
              render={({ field: { onChange, value } }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label='From'
                    inputFormat={`${dateFormatWithZeros} ${timeFormat}`}
                    value={value}
                    onChange={onChange}
                    renderInput={(props) => <TextField {...props} />}
                    ampm={ampm}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              name='toDate'
              control={control}
              render={({ field: { onChange, value } }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label='To'
                    inputFormat={`${dateFormatWithZeros} ${timeFormat}`}
                    value={value}
                    onChange={onChange}
                    renderInput={(props) => <TextField {...props} />}
                    ampm={ampm}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            reset();
            handleClose();
          }}
          color='secondary'
          variant='contained'
        >
          Cancel
        </Button>
        <LoadingButton
          sx={{ minWidth: '10rem' }}
          variant='contained'
          color='primary'
          disabled={isSubmitted}
          loading={isLoading}
          loadingPosition='start'
          startIcon={<div />}
          onClick={async () => {
            await handleSubmit(onSubmit)();
            reset();
            handleClose();
          }}
        >
          Export
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ExportWeatherDialog;
